/**
 * Per-locale widget string defaults (#1262).
 *
 * Widgets keep their per-widget `translations` setting — a store of the shape
 * `Record<key, Record<locale, string>>` that the operator edits in the
 * dashboard and `useTranslation` (widget-core) resolves with
 * `store[key][locale] || store[key].en_US || default`. This module adds the
 * layer under it: a table of strings shipped in code for each locale we
 * support, merged into the store BEFORE it reaches `_t`, so the resolution
 * order at render is
 *
 *   1. the operator's value for the visitor locale (non-blank)
 *   2. the operator's value for another locale of the same language (non-blank)
 *   3. the shipped table for the locale (exact, else same language)
 *   4. the schema's en_US default
 *
 * No behavior change where nothing is shipped: `en_US` and any locale without
 * a table return the operator store as-is (same object), and an operator who
 * already filled a locale keeps every one of their strings.
 *
 * THE GATE (Eli, 2026-08-27): the shipped tables apply only on the CONFIGURED
 * path — the operator turned `language.useAutoDetectedLocale` off and set
 * `language.locale`. Visitors on the auto-detected path keep the English
 * defaults (their operator-typed strings still apply, as they always did)
 * until the Central content fill (central#3141) exists, mirroring the WP
 * engine's coverage gate: chrome must not switch language ahead of content.
 *
 * Adding a locale = one file (`./<locale>.ts`) + one entry in LOCALE_TABLES.
 */
import { useMemo } from 'react'
import { useTranslation } from '../../hooks/locale'
import { es_ES } from './es_ES'
import { nl_NL } from './nl_NL'
import { de_DE } from './de_DE'
import type { LocaleTable, TranslationTableId } from './tables'

export type { LocaleTable, TranslationTableId, WidgetStrings, WidgetTranslationKeys } from './tables'

/** Every locale shipped in code, keyed by `xx_YY`. */
export const LOCALE_TABLES: Readonly<Record<string, LocaleTable>> = Object.freeze({ es_ES, nl_NL, de_DE })

type TranslationStore<K extends string> = Record<K, Record<string, string>>

function languageOf(locale: string): string {
  return (locale.split(/[_-]/)[0] ?? '').toLowerCase()
}

/**
 * The shipped table for a locale: exact match, else the first table of the
 * same language (`es_MX` → `es_ES`, `nl_BE` → `nl_NL`, `de_AT` → `de_DE`). Never crosses languages — a visitor in
 * `fr_FR` with no French table shipped gets the en_US defaults, as today.
 */
export function matchLocaleTable(locale: string): LocaleTable | null {
  const id = matchLocaleId(locale)
  return id === null ? null : LOCALE_TABLES[id]
}

/**
 * The id of the table `matchLocaleTable` picks for `locale`: the locale
 * itself when a table ships for it, else the first same-language table
 * (`es_MX` → `'es_ES'`), else null. Exported so the dashboard editor can say
 * which table a same-language fallback came from (#1270). Same lookup
 * `matchLocaleTable` always did, so its answers are unchanged for every input.
 */
export function matchLocaleId(locale: string): string | null {
  if (LOCALE_TABLES[locale]) return locale
  const lang = languageOf(locale)
  if (lang === '') return null
  for (const id of Object.keys(LOCALE_TABLES)) {
    if (languageOf(id) === lang) return id
  }
  return null
}

function nonBlank(value: unknown): string | null {
  return typeof value === 'string' && value.trim() !== '' ? value : null
}

/**
 * The locale key whose operator text wins for `locale`: the exact locale when
 * its value is non-blank, else the first other locale of the same language
 * with a non-blank value, else null. Exported so the dashboard editor can say
 * which locale a blank field falls back to (#1270).
 */
export function operatorLocaleFor(entry: Record<string, string> | undefined, locale: string): string | null {
  if (!entry) return null
  if (nonBlank(entry[locale]) !== null) return locale
  const lang = languageOf(locale)
  for (const [entryLocale, value] of Object.entries(entry)) {
    if (entryLocale === locale || languageOf(entryLocale) !== lang) continue
    if (nonBlank(value) !== null) return entryLocale
  }
  return null
}

/**
 * The operator's own string for `locale`: the exact locale first, else any
 * other locale of the same language (an operator who typed `es_ES` is not
 * beaten by the shipped table for an `es_MX` visitor).
 */
function operatorValue(entry: Record<string, string> | undefined, locale: string): string | null {
  const source = operatorLocaleFor(entry, locale)
  return source === null ? null : (entry as Record<string, string>)[source]
}

/**
 * The operator store with the shipped defaults for `locale` filled in under
 * the `locale` key, for every key the table covers and the operator left
 * blank. Pure; never mutates `store`; returns `store` itself when there is
 * nothing to add.
 */
export function withLocaleDefaults<K extends string>(
  widget: TranslationTableId,
  store: TranslationStore<K> | undefined,
  locale: string,
): TranslationStore<K> {
  const base = (store ?? {}) as TranslationStore<K>
  const strings = matchLocaleTable(locale)?.[widget] as Readonly<Record<string, string>> | undefined
  if (!strings) return base
  let merged: TranslationStore<K> | null = null
  for (const [key, shipped] of Object.entries(strings)) {
    if (nonBlank(shipped) === null) continue
    const entry = base[key as K]
    const value = operatorValue(entry, locale) ?? shipped
    if (entry?.[locale] === value) continue
    merged ??= { ...base }
    merged[key as K] = { ...(entry ?? {}), [locale]: value }
  }
  return merged ?? base
}

/** The part of the widget's `language` setting the gate reads. */
export interface LanguageGate {
  useAutoDetectedLocale: boolean
}

/**
 * The store `_t` should resolve against: the operator store with the shipped
 * defaults merged in when the locale was configured explicitly, the operator
 * store untouched when it was auto-detected. Pure; exported for tests.
 */
export function resolveTranslationStore<K extends string>(
  widget: TranslationTableId,
  store: TranslationStore<K> | undefined,
  locale: string,
  language: LanguageGate,
): TranslationStore<K> {
  if (language.useAutoDetectedLocale) return (store ?? {}) as TranslationStore<K>
  return withLocaleDefaults(widget, store, locale)
}

/**
 * Drop-in for `useTranslation(store, locale)` that resolves against the
 * shipped locale tables on the configured path. `widget` names the table
 * (see WidgetTranslationKeys); `store` is the widget's `options.translations`;
 * `locale` is what `useLocaleDetection` resolved; `language` is the widget's
 * Language setting (the gate reads `useAutoDetectedLocale`).
 */
export function useLocalizedTranslation<K extends string>(
  widget: TranslationTableId,
  store: TranslationStore<K> | undefined,
  locale: string = 'en_US',
  language: LanguageGate,
) {
  const configured = !language.useAutoDetectedLocale
  const merged = useMemo(
    () => resolveTranslationStore(widget, store, locale, { useAutoDetectedLocale: !configured }),
    [widget, store, locale, configured],
  )
  return useTranslation(merged, locale)
}
